import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Loader2 } from 'lucide-react';
import Navbar from '../components/site/Navbar';
import { Footer } from '../components/site/FooterCTA';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Blog — YouBuild';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Notes from a non-technical founder building YouBuild in public — lessons, product updates, and the reality of shipping software without writing code.');

    (async () => {
      try {
        const res = await axios.get(`${API}/blog/posts`);
        setPosts(res.data?.posts || []);
      } catch (e) {
        setError('Could not load posts right now. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen font-sans antialiased" style={{ backgroundColor: 'var(--yb-bg)', color: 'var(--yb-text)' }}>
      <Navbar />
      <main>
        <section className="pt-32 pb-14 lg:pt-40 lg:pb-16">
          <div className="max-w-[900px] mx-auto px-6 lg:px-10">
            <p className="text-[13px] uppercase tracking-widest mb-4" style={{ color: 'var(--yb-muted)' }}>The YouBuild blog</p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.02em]" style={{ color: 'var(--yb-ink)' }}>
              Notes from<br />
              <span className="italic">a non-technical founder.</span>
            </h1>
            <p className="mt-6 text-[16px] md:text-[17px] max-w-2xl leading-relaxed" style={{ color: 'var(--yb-muted)' }}>
              What I’m learning as I build YouBuild in public — lessons, product updates, and the reality of shipping software without writing code.
            </p>
          </div>
        </section>

        <section className="pb-24 lg:pb-32">
          <div className="max-w-[900px] mx-auto px-6 lg:px-10">
            {loading && (
              <div className="flex items-center justify-center py-24" style={{ color: 'var(--yb-muted)' }}>
                <Loader2 className="animate-spin" />
              </div>
            )}
            {error && !loading && (
              <p className="text-[14px]" style={{ color: '#B4530A' }}>{error}</p>
            )}
            {!loading && !error && posts.length === 0 && (
              <div className="rounded-2xl border border-dashed p-10 text-center" style={{ borderColor: '#CFCDC5' }}>
                <p className="font-serif text-2xl" style={{ color: 'var(--yb-ink)' }}>
                  No posts yet. <span className="italic">Check back soon.</span>
                </p>
              </div>
            )}
            {!loading && posts.length > 0 && (
              <ul className="divide-y" style={{ borderColor: 'var(--yb-border)' }}>
                {posts.map((p, idx) => (
                  <li key={p.id} className={`py-8 ${idx !== 0 ? 'border-t' : ''}`} style={{ borderColor: 'var(--yb-border)' }}>
                    <Link to={`/blog/${p.slug}`} className="group grid grid-cols-1 md:grid-cols-[1fr_180px] gap-6 items-start">
                      <div>
                        <div className="flex items-center gap-2 text-[12.5px] mb-3" style={{ color: 'var(--yb-muted)' }}>
                          <Calendar size={13} />
                          <span>{formatDate(p.published_at)}</span>
                        </div>
                        <h2 className="font-serif text-3xl md:text-4xl leading-tight tracking-tight group-hover:opacity-80 transition-opacity" style={{ color: 'var(--yb-ink)' }}>
                          {p.title}
                        </h2>
                        <p className="mt-3 text-[15.5px] leading-relaxed" style={{ color: 'var(--yb-muted)' }}>
                          {p.excerpt}
                        </p>
                        <span className="mt-4 inline-flex items-center gap-1.5 text-[13.5px] font-medium" style={{ color: 'var(--yb-indigo)' }}>
                          Read post <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                        </span>
                      </div>
                      {p.cover_image_url && (
                        <div className="aspect-[4/3] rounded-xl overflow-hidden border" style={{ borderColor: 'var(--yb-border)' }}>
                          <img src={p.cover_image_url} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Blog;
